'use client'

import React from 'react'
import { InfoHint } from './InfoHint'

export interface RecordField {
  label: string
  value: React.ReactNode
  hint?: string
  /** Spans both columns — for a value that needs the full width, like a date range. */
  wide?: boolean
}

export interface RecordCardProps {
  title: React.ReactNode
  subtitle?: React.ReactNode
  /** Thumbnail, channel logo or avatar, shown beside the title. */
  media?: React.ReactNode
  status?: { label: string; color?: string }
  fields: RecordField[]
  /** Row of buttons under the fields; wraps, each at least 44px tall. */
  actions?: React.ReactNode
  className?: string
}

const Field: React.FC<RecordField> = ({ label, value, hint, wide }) => (
  <div className={`flex flex-col gap-0.5 min-w-0 ${wide ? 'col-span-2' : ''}`}>
    <span className="inline-flex items-center gap-1 text-[11px] font-normal text-dashboard-faint font-poppins">
      {label}
      {hint && <InfoHint label={hint} ariaLabel={`What is ${label}?`} />}
    </span>
    <span className="text-[12px] font-medium text-dashboard-text font-poppins truncate">
      {value}
    </span>
  </div>
)

/**
 * A table row rendered as a card.
 *
 * This is the mobile half of every list in the dashboard. A phone cannot show a
 * seven-column table without sideways scrolling, and sideways scrolling inside a
 * vertically scrolling page is the single worst thing these screens did — so
 * below `md` the row becomes this instead: same fields, stacked two-up, with the
 * identity (title, media, status) on top where it can be scanned.
 *
 * `min-w-0` and `truncate` throughout, because a long property name would
 * otherwise widen the grid track and push the card past the viewport.
 */
export const RecordCard: React.FC<RecordCardProps> = ({
  title,
  subtitle,
  media,
  status,
  fields,
  actions,
  className = '',
}) => (
  <div
    className={`flex flex-col gap-3 rounded-xl border border-[#E6ECF1] bg-white p-3.5 ${className}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-start gap-2.5 min-w-0">
        {media && <span className="shrink-0">{media}</span>}
        <div className="flex flex-col min-w-0 gap-0.5">
          <span className="text-[14px] font-semibold text-[#000D33] font-poppins truncate">
            {title}
          </span>
          {subtitle && (
            <span className="text-[12px] font-normal text-dashboard-muted font-poppins truncate">
              {subtitle}
            </span>
          )}
        </div>
      </div>
      {status && (
        <span
          className="px-2 py-0.5 rounded-[26px] text-[11px] font-medium text-white font-poppins leading-4 flex-shrink-0"
          style={{ backgroundColor: status.color || 'var(--dashboard-text-muted)' }}
        >
          {status.label}
        </span>
      )}
    </div>

    {fields.length > 0 && (
      <div className="grid grid-cols-2 gap-2 border-t border-[#EEF2F6] pt-3">
        {fields.map((field) => (
          <Field key={field.label} {...field} />
        ))}
      </div>
    )}

    {actions && (
      <div className="flex flex-wrap items-center gap-2 border-t border-[#EEF2F6] pt-3">
        {actions}
      </div>
    )}
  </div>
)
